'use client';

import * as React from 'react';
import {
  Calendar as CalendarIcon,
  ChevronDown,
  Check,
  RotateCcw,
} from 'lucide-react';
import type { DateRange as DateRangeType } from 'react-day-picker';
import type { Locale } from '@ufc/i18n';
import { getTranslation, useLocale } from '@ufc/i18n';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DateRangePreset {
  label: string;
  value: () => { from: Date; to: Date };
}

export interface DateRangePickerProps {
  /** Selected date range */
  dateRange?: { from: Date | undefined; to: Date | undefined };
  /** Date range change handler */
  onDateRangeChange?: (range: {
    from: Date | undefined;
    to: Date | undefined;
  }) => void;
  /** Preset ranges */
  presets?: DateRangePreset[];
  /** Additional className */
  className?: string;
  /** Is RTL mode */
  isRTL?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Label for preset ranges section */
  presetsLabel?: string;
  /** Label for start date */
  startLabel?: string;
  /** Label for end date */
  endLabel?: string;
  /** Label for clear button */
  clearLabel?: string;
  /** Placeholder for unselected date */
  selectDatePlaceholder?: string;
}

function normalizeDate(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function resolveLocaleCode(locale: Locale): string {
  if (locale === 'ar') return 'ar-SA';
  if (locale === 'ur') return 'ur-PK';
  return 'en-US';
}

function formatDateDisplay(
  date: Date | undefined,
  localeCode: string
): string {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(localeCode, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toLocaleDateString(localeCode, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}

function formatWeekdayDisplay(value: Date, localeCode: string): string {
  const isRtlLocale =
    localeCode.startsWith('ar') || localeCode.startsWith('ur');

  if (isRtlLocale) {
    return value.toLocaleDateString(localeCode, { weekday: 'narrow' });
  }

  return value
    .toLocaleDateString(localeCode, { weekday: 'short' })
    .slice(0, 2);
}

const MOBILE_LAYOUT_BREAKPOINT = 640;

function useCompactDateRangeLayout(): boolean {
  const [isCompact, setIsCompact] = React.useState(false);

  React.useEffect(() => {
    const updateCompactLayout = () => {
      setIsCompact(window.innerWidth < MOBILE_LAYOUT_BREAKPOINT);
    };

    updateCompactLayout();
    window.addEventListener('resize', updateCompactLayout);
    return () => window.removeEventListener('resize', updateCompactLayout);
  }, []);

  return isCompact;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  presets = [],
  className,
  isRTL,
  placeholder,
  presetsLabel,
  startLabel,
  endLabel,
  clearLabel,
  selectDatePlaceholder,
}: DateRangePickerProps) {
  const { t, dir, locale } = useLocale();
  const documentLocale =
    typeof document !== 'undefined'
    && (document.documentElement.lang === 'ar'
      || document.documentElement.lang === 'ur'
      || document.documentElement.lang === 'en')
      ? (document.documentElement.lang as Locale)
      : null;

  const activeLocale = documentLocale ?? locale;
  const activeLocaleCode = resolveLocaleCode(activeLocale);
  const translate = React.useCallback(
    (key: string) =>
      documentLocale ? getTranslation(documentLocale, key) : t(key),
    [documentLocale, t]
  );

  // Use i18n translations with prop overrides
  const effectivePlaceholder =
    placeholder ?? translate('dateRangePicker.placeholder');
  const effectivePresetsLabel =
    presetsLabel ?? translate('dateRangePicker.presetsLabel');
  const effectiveStartLabel =
    startLabel ?? translate('dateRangePicker.startLabel');
  const effectiveEndLabel = endLabel ?? translate('dateRangePicker.endLabel');
  const effectiveClearLabel =
    clearLabel ?? translate('dateRangePicker.clearLabel');
  const effectiveSelectDatePlaceholder =
    selectDatePlaceholder ?? translate('dateRangePicker.selectDatePlaceholder');

  // Use dir from i18n context if isRTL not explicitly provided
  const effectiveRTL = isRTL ?? dir === 'rtl';
  const isMobile = useCompactDateRangeLayout();
  const [open, setOpen] = React.useState(false);

  // Internal selection state for the calendar
  const [pendingRange, setPendingRange] = React.useState<
    DateRangeType | undefined
  >(undefined);

  // Controlled month state to prevent blinking from defaultMonth creating new Date objects
  const [month, setMonth] = React.useState<Date>(
    () => dateRange?.from || new Date()
  );

  // react-day-picker v9 (addToRange with min=0) emits { from: A, to: A } on the
  // FIRST click when nothing is selected. Double-click detection works by:
  // 1st click → from===to → store date, reset pendingRange to { from, to: undefined }
  // 2nd click on same date → from===to again (via the "from set, !to" path) → fire
  const sameClickRef = React.useRef<number | null>(null);

  // When popover opens, initialize pending with current dateRange and reset click tracking
  React.useEffect(() => {
    if (open) {
      setPendingRange(
        dateRange?.from
          ? {
              from: normalizeDate(dateRange.from),
              to: dateRange.to ? normalizeDate(dateRange.to) : undefined,
            }
          : undefined
      );
      sameClickRef.current = null;
      if (dateRange?.from) {
        setMonth(dateRange.from);
      }
    }
  }, [open, dateRange?.from, dateRange?.to]);

  const handleSelect = (range: DateRangeType | undefined) => {
    if (!range?.from) {
      // Cleared or empty
      setPendingRange(undefined);
      sameClickRef.current = null;
      return;
    }

    const from = normalizeDate(range.from);

    if (!range.to) {
      // Only start is set — normal "waiting for end" state
      setPendingRange(range);
      sameClickRef.current = from.getTime();
      return;
    }

    const to = normalizeDate(range.to);

    if (from.getTime() !== to.getTime()) {
      // Different start and end — fire immediately
      sameClickRef.current = null;
      setPendingRange(range);
      onDateRangeChange?.({ from, to });
      setOpen(false);
      return;
    }

    // from === to (react-day-picker emitted same-day range)
    const ts = from.getTime();

    if (sameClickRef.current === ts) {
      // 2nd click on same date → double-click confirmed → fire same-day
      sameClickRef.current = null;
      setPendingRange({ from, to });
      onDateRangeChange?.({ from, to });
      setOpen(false);
    } else {
      // 1st click: record date and reset to "start-only" so the NEXT click on
      // same date goes through the "from set, to undefined" → isSameDay path,
      // emitting { from, to } again which we'll catch above.
      sameClickRef.current = ts;
      setPendingRange({ from, to: undefined });
    }
  };

  const handlePreset = (preset: DateRangePreset) => {
    const range = preset.value();
    const from = normalizeDate(range.from);
    const to = normalizeDate(range.to);

    setPendingRange({ from, to });
    onDateRangeChange?.({ from, to });
    setOpen(false);
  };

  const handleClear = () => {
    setPendingRange(undefined);
    onDateRangeChange?.({ from: undefined, to: undefined });
  };

  // Display values: show pending while open, otherwise show dateRange
  const displayRange = open
    ? pendingRange
    : dateRange?.from
    ? { from: dateRange.from, to: dateRange.to }
    : undefined;

  const displayText =
    dateRange?.from && dateRange?.to
      ? `${formatDateDisplay(
          dateRange.from,
          activeLocaleCode
        )} - ${formatDateDisplay(
          dateRange.to,
          activeLocaleCode
        )}`
      : effectivePlaceholder;
  const hasSelection = Boolean(displayRange?.from || displayRange?.to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-10 px-3 bg-background border border-input rounded-lg font-normal',
            'transition-colors justify-start',
            'hover:bg-accent/70 dark:hover:bg-slate-800 dark:hover:border-slate-600',
            !dateRange?.from && !dateRange?.to && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon
            className={cn(
              'w-4 h-4 flex-shrink-0 text-muted-foreground',
              effectiveRTL ? 'ms-2' : 'me-2'
            )}
          />
          <span
            className={cn(
              'flex-1 text-sm truncate',
              effectiveRTL ? 'text-right' : 'text-left'
            )}
          >
            {displayText}
          </span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground flex-shrink-0',
              effectiveRTL ? 'me-2' : 'ms-2'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 border border-border/50 shadow-xl rounded-xl overflow-hidden bg-white dark:bg-slate-900',
          isMobile
            ? 'w-[calc(100vw-16px)] max-w-[calc(100vw-16px)]'
            : 'w-auto max-w-[760px]',
          effectiveRTL && 'text-right'
        )}
        align={effectiveRTL ? 'end' : 'start'}
        sideOffset={isMobile ? 4 : 8}
      >
        <div
          className={cn(
            'bg-white dark:bg-slate-900',
            isMobile ? 'flex flex-col' : 'flex'
          )}
        >
          {/* Preset Ranges */}
          {presets.length > 0 && (
            <div
              className={cn(
                'border-border/50 bg-slate-50 dark:bg-slate-800/50 flex-shrink-0',
                isMobile
                  ? 'w-full py-2 px-3 border-b'
                  : cn(
                      'py-3 px-2 w-[160px]',
                      effectiveRTL ? 'border-s' : 'border-e'
                    )
              )}
            >
              <div className="text-[10px] font-semibold text-muted-foreground mb-2 px-2 uppercase tracking-wider">
                {effectivePresetsLabel}
              </div>
              <div
                className={cn(
                  isMobile ? 'grid grid-cols-2 gap-1' : 'flex flex-col gap-0.5'
                )}
              >
                {presets.map((preset, index) => {
                  const presetRange = preset.value();
                  const isActive =
                    dateRange?.from &&
                    dateRange?.to &&
                    normalizeDate(dateRange.from).getTime() ===
                      normalizeDate(presetRange.from).getTime() &&
                    normalizeDate(dateRange.to).getTime() ===
                      normalizeDate(presetRange.to).getTime();

                  return (
                    <button
                      key={index}
                      onClick={() => handlePreset(preset)}
                      className={cn(
                        'flex items-center gap-2 text-xs px-2 rounded-md transition-all font-normal text-start w-full',
                        isMobile ? 'h-9 justify-center' : 'h-8',
                        isActive
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-muted dark:hover:bg-slate-700/70 text-foreground'
                      )}
                    >
                      {isActive && !isMobile && (
                        <Check className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                      <span
                        className={cn(
                          !isActive &&
                            !isMobile &&
                            (effectiveRTL ? 'me-5.5' : 'ms-5.5')
                        )}
                      >
                        {preset.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calendar */}
          <div
            className={cn(
              'bg-white dark:bg-slate-900 min-w-0',
              isMobile ? 'p-3' : 'p-4'
            )}
          >
            <Calendar
              mode="range"
              month={month}
              onMonthChange={setMonth}
              selected={displayRange}
              onSelect={handleSelect}
              numberOfMonths={isMobile ? 1 : 2}
              showOutsideDays={false}
              formatters={{
                formatCaption: (value) =>
                  value.toLocaleDateString(activeLocaleCode, {
                    month: 'long',
                    year: 'numeric',
                  }),
                formatWeekdayName: (value) =>
                  formatWeekdayDisplay(value, activeLocaleCode),
              }}
              className={cn(isMobile && 'p-0')}
            />

            {/* Footer with selected range display */}
            <div
              className={cn(
                'mt-4 pt-3 border-t border-border/50',
                isMobile
                  ? 'flex flex-col gap-3'
                  : 'flex items-center justify-between'
              )}
            >
              <div
                className={cn(
                  'flex items-center gap-3',
                  isMobile && 'justify-between'
                )}
              >
                {/* Start date */}
                <div className="flex flex-col">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {effectiveStartLabel}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-medium px-2.5 py-1 rounded-md min-w-[100px] text-center',
                      displayRange?.from
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted/50 text-muted-foreground'
                    )}
                  >
                    {displayRange?.from
                      ? formatDateDisplay(displayRange.from, activeLocaleCode)
                      : effectiveSelectDatePlaceholder}
                  </span>
                </div>

                {/* Arrow */}
                <div
                  className={cn(
                    'text-muted-foreground mt-4',
                    isMobile && 'mt-0'
                  )}
                >
                  →
                </div>

                {/* End date */}
                <div className="flex flex-col">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {effectiveEndLabel}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-medium px-2.5 py-1 rounded-md min-w-[100px] text-center',
                      displayRange?.to
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted/50 text-muted-foreground'
                    )}
                  >
                    {displayRange?.to
                      ? formatDateDisplay(displayRange.to, activeLocaleCode)
                      : effectiveSelectDatePlaceholder}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={!hasSelection}
                aria-label={effectiveClearLabel}
                className={cn(
                  'h-9 px-3.5 text-sm font-semibold gap-1.5',
                  'border-destructive/30 text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20 hover:text-destructive',
                  'disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent',
                  isMobile && 'w-full'
                )}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {effectiveClearLabel}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
